import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from "react";

import playgroundApi from "../api/playgroundApi";
import type {
  PlaygroundConversation,
  PlaygroundFileUpload,
  PlaygroundMessage
} from "../types";

/**
 * State for the Athena archive: the list of past conversations, the one being
 * read, and its messages and uploads. Athena is read-only, so the only writes
 * are rename and delete.
 */
type PlaygroundContextValue = {
  conversations: PlaygroundConversation[];
  selectedConversationId: string | null;
  selectedConversation: PlaygroundConversation | null;
  messages: PlaygroundMessage[];
  fileUploadsById: Record<string, PlaygroundFileUpload>;

  isLoadingConversations: boolean;
  hasLoadedConversations: boolean;
  isLoadingConversationData: boolean;
  error: string | null;

  refreshConversations: () => Promise<void>;
  selectConversation: (playgroundConversationId: string) => Promise<void>;
  closeConversation: () => void;
  renameConversation: (args: {
    playgroundConversationId: string;
    title: string | null;
  }) => Promise<void>;
  deleteConversation: (args: {
    playgroundConversationId: string;
  }) => Promise<void>;
};

const PlaygroundContext = createContext<PlaygroundContextValue | null>(null);

function normalizeMessages(
  apiMessages: PlaygroundMessage[]
): PlaygroundMessage[] {
  // Backend commonly returns newest-first; UI is easiest to render oldest-first.
  return [...apiMessages].reverse();
}

export function PlaygroundProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [conversations, setConversations] = useState<PlaygroundConversation[]>(
    []
  );
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);
  // Always-current ref to avoid stale-closure races in async flows.
  const selectedConversationIdRef = useRef<string | null>(null);
  // Incrementing token to invalidate in-flight loads when switching threads.
  const conversationLoadSeqRef = useRef(0);
  const [messagesByConversationId, setMessagesByConversationId] = useState<
    Record<string, PlaygroundMessage[]>
  >({});
  const [fileUploadsByConversationId, setFileUploadsByConversationId] =
    useState<Record<string, Record<string, PlaygroundFileUpload>>>({});

  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [hasLoadedConversations, setHasLoadedConversations] = useState(false);
  const [isLoadingConversationData, setIsLoadingConversationData] =
    useState(false);
  const allowAutoSelectConversationRef = useRef(true);
  const [error, setError] = useState<string | null>(null);

  const selectedConversation = useMemo(
    () =>
      conversations.find(
        (c) => c.playground_conversation_id === selectedConversationId
      ) ?? null,
    [conversations, selectedConversationId]
  );

  const messages = useMemo(() => {
    if (!selectedConversationId) return [];
    return messagesByConversationId[selectedConversationId] ?? [];
  }, [messagesByConversationId, selectedConversationId]);

  const fileUploadsById = useMemo(() => {
    if (!selectedConversationId) return {};
    return fileUploadsByConversationId[selectedConversationId] ?? {};
  }, [fileUploadsByConversationId, selectedConversationId]);

  const setSelectedConversationIdSafe = useCallback((id: string | null) => {
    selectedConversationIdRef.current = id;
    setSelectedConversationId(id);
  }, []);

  const refreshConversations = useCallback(async () => {
    setIsLoadingConversations(true);
    setError(null);
    try {
      const data = await playgroundApi.listConversations({ limit: 50 });
      setConversations(data);

      // Open the most recent conversation when nothing is selected yet.
      if (
        allowAutoSelectConversationRef.current &&
        !selectedConversationIdRef.current &&
        data.length > 0
      ) {
        setSelectedConversationIdSafe(data[0].playground_conversation_id);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load conversations");
    } finally {
      setIsLoadingConversations(false);
      setHasLoadedConversations(true);
    }
  }, [setSelectedConversationIdSafe]);

  const selectConversation = useCallback(
    async (playgroundConversationId: string) => {
      conversationLoadSeqRef.current += 1;
      const loadSeq = conversationLoadSeqRef.current;

      setSelectedConversationIdSafe(playgroundConversationId);
      allowAutoSelectConversationRef.current = true;
      setError(null);

      // Don't briefly show the previous thread while this one loads.
      setMessagesByConversationId((prev) => ({
        ...prev,
        [playgroundConversationId]: prev[playgroundConversationId] ?? []
      }));
      setFileUploadsByConversationId((prev) => ({
        ...prev,
        [playgroundConversationId]: prev[playgroundConversationId] ?? {}
      }));

      const isStale = () =>
        conversationLoadSeqRef.current !== loadSeq ||
        selectedConversationIdRef.current !== playgroundConversationId;

      setIsLoadingConversationData(true);
      try {
        const [msgs, uploads] = await Promise.all([
          playgroundApi.listMessages({ playgroundConversationId, limit: 200 }),
          playgroundApi.listFileUploads({
            playgroundConversationId,
            limit: 200
          })
        ]);
        if (isStale()) return;

        setMessagesByConversationId((prev) => ({
          ...prev,
          [playgroundConversationId]: normalizeMessages(msgs)
        }));
        const byId: Record<string, PlaygroundFileUpload> = {};
        uploads.forEach((u) => {
          byId[u.playground_file_upload_id] = u;
        });
        setFileUploadsByConversationId((prev) => ({
          ...prev,
          [playgroundConversationId]: byId
        }));
      } catch (e) {
        if (isStale()) return;
        setError(
          e instanceof Error ? e.message : "Failed to load conversation"
        );
      } finally {
        if (!isStale()) setIsLoadingConversationData(false);
      }
    },
    [setSelectedConversationIdSafe]
  );

  const closeConversation = useCallback(() => {
    conversationLoadSeqRef.current += 1;
    allowAutoSelectConversationRef.current = false;
    setSelectedConversationIdSafe(null);
    setIsLoadingConversationData(false);
    setError(null);
  }, [setSelectedConversationIdSafe]);

  const renameConversation = useCallback(
    async (args: {
      playgroundConversationId: string;
      title: string | null;
    }) => {
      const updated = await playgroundApi.updateConversation({
        playgroundConversationId: args.playgroundConversationId,
        title: args.title
      });
      setConversations((prev) =>
        prev.map((c) =>
          c.playground_conversation_id === updated.playground_conversation_id
            ? updated
            : c
        )
      );
    },
    []
  );

  const deleteConversation = useCallback(
    async (args: { playgroundConversationId: string }) => {
      const id = args.playgroundConversationId;
      if (!id) return;

      conversationLoadSeqRef.current += 1;
      const wasSelected = selectedConversationIdRef.current === id;

      // Optimistically remove from the list.
      setConversations((prev) =>
        prev.filter((c) => c.playground_conversation_id !== id)
      );
      setMessagesByConversationId((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setFileUploadsByConversationId((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });

      if (wasSelected) {
        setSelectedConversationIdSafe(null);
        setIsLoadingConversationData(false);
        // After deleting the open conversation, show the empty state rather
        // than jumping to another thread.
        allowAutoSelectConversationRef.current = false;
      }

      setError(null);
      try {
        await playgroundApi.deleteConversation({
          playgroundConversationId: id
        });
      } catch (e) {
        setError(
          e instanceof Error ? e.message : "Failed to delete conversation"
        );
      } finally {
        await refreshConversations().catch(() => {});
      }
    },
    [refreshConversations, setSelectedConversationIdSafe]
  );

  const value = useMemo<PlaygroundContextValue>(
    () => ({
      conversations,
      selectedConversationId,
      selectedConversation,
      messages,
      fileUploadsById,
      isLoadingConversations,
      hasLoadedConversations,
      isLoadingConversationData,
      error,
      refreshConversations,
      selectConversation,
      closeConversation,
      renameConversation,
      deleteConversation
    }),
    [
      conversations,
      selectedConversationId,
      selectedConversation,
      messages,
      fileUploadsById,
      isLoadingConversations,
      hasLoadedConversations,
      isLoadingConversationData,
      error,
      refreshConversations,
      selectConversation,
      closeConversation,
      renameConversation,
      deleteConversation
    ]
  );

  return (
    <PlaygroundContext.Provider value={value}>
      {children}
    </PlaygroundContext.Provider>
  );
}

export function usePlaygroundContext(): PlaygroundContextValue {
  const ctx = useContext(PlaygroundContext);
  if (!ctx) {
    throw new Error(
      "usePlaygroundContext must be used within PlaygroundProvider"
    );
  }
  return ctx;
}
